import type { Car } from '@multirx/shared';
import { carPower } from '@multirx/shared/types/car.js';

export type CarListSort = 'name-asc' | 'name-desc' | 'power-asc' | 'power-desc';

/** Filtre par bande de puissance totale (moteur + pneu). */
export type CarPowerBand = 'all' | '2-4' | '4-6' | '6-8' | '8-10';

export interface CarListFilters {
  search: string;
  powerBand: CarPowerBand;
  sort: CarListSort;
}

export const DEFAULT_CAR_LIST_FILTERS: CarListFilters = {
  search: '',
  powerBand: 'all',
  sort: 'power-desc',
};

const POWER_BANDS: Record<CarPowerBand, { min: number; max: number; label: string } | null> = {
  all: null,
  '2-4': { min: 2, max: 4, label: '2–4★' },
  '4-6': { min: 4, max: 6, label: '4–6★' },
  '6-8': { min: 6, max: 8, label: '6–8★' },
  '8-10': { min: 8, max: 10, label: '8–10★' },
};

export function applyCarListFilters(cars: Car[], filters: CarListFilters): Car[] {
  const q = filters.search.trim().toLowerCase();
  const band = POWER_BANDS[filters.powerBand];

  let list = cars.filter((c) => {
    if (q && !c.name.toLowerCase().includes(q) && !String(c.id ?? '').includes(q)) {
      return false;
    }
    const power = carPower(c);
    if (band && (power < band.min || power > band.max)) {
      return false;
    }
    return true;
  });

  list = [...list].sort((a, b) => {
    switch (filters.sort) {
      case 'name-asc':
        return a.name.localeCompare(b.name, 'fr');
      case 'name-desc':
        return b.name.localeCompare(a.name, 'fr');
      case 'power-asc':
        return carPower(a) - carPower(b) || (a.id ?? 0) - (b.id ?? 0);
      case 'power-desc':
      default:
        return carPower(b) - carPower(a) || (a.id ?? 0) - (b.id ?? 0);
    }
  });

  return list;
}

export interface CarListToolbarHandle {
  getFilters: () => CarListFilters;
  setFilters: (filters: CarListFilters) => void;
}

/** Barre recherche + filtres puissance + tri (fixée en haut de la sidebar). */
export function mountCarListToolbar(
  container: HTMLElement,
  initial: CarListFilters,
  onChange: (filters: CarListFilters) => void
): CarListToolbarHandle {
  container.innerHTML = '';
  container.className = 'garage-sidebar-toolbar';

  let filters = { ...initial };

  const searchInput = document.createElement('input');
  searchInput.type = 'search';
  searchInput.className = 'garage-toolbar-search';
  searchInput.placeholder = 'Rechercher par nom ou n°…';
  searchInput.autocomplete = 'off';
  searchInput.value = filters.search;

  const rowFilters = document.createElement('div');
  rowFilters.className = 'garage-toolbar-row';

  const powerLabel = document.createElement('label');
  powerLabel.className = 'garage-toolbar-label';
  powerLabel.textContent = 'Puissance';

  const powerSelect = document.createElement('select');
  powerSelect.className = 'garage-toolbar-select';
  powerSelect.setAttribute('aria-label', 'Filtrer par puissance totale');
  for (const [key, meta] of Object.entries(POWER_BANDS)) {
    const opt = document.createElement('option');
    opt.value = key;
    opt.textContent = meta ? meta.label : 'Toutes';
    powerSelect.appendChild(opt);
  }
  powerSelect.value = filters.powerBand;

  const sortLabel = document.createElement('label');
  sortLabel.className = 'garage-toolbar-label';
  sortLabel.textContent = 'Tri';

  const sortSelect = document.createElement('select');
  sortSelect.className = 'garage-toolbar-select';
  sortSelect.setAttribute('aria-label', 'Trier la liste');
  sortSelect.innerHTML = `
    <option value="power-desc">Puissance ↓</option>
    <option value="power-asc">Puissance ↑</option>
    <option value="name-asc">Nom A→Z</option>
    <option value="name-desc">Nom Z→A</option>`;
  sortSelect.value = filters.sort;

  powerLabel.appendChild(powerSelect);
  sortLabel.appendChild(sortSelect);
  rowFilters.append(powerLabel, sortLabel);

  const countEl = document.createElement('p');
  countEl.className = 'garage-toolbar-count';
  countEl.setAttribute('aria-live', 'polite');

  function emit(): void {
    filters = {
      search: searchInput.value,
      powerBand: powerSelect.value as CarPowerBand,
      sort: sortSelect.value as CarListSort,
    };
    onChange(filters);
  }

  searchInput.addEventListener('input', emit);
  powerSelect.addEventListener('change', emit);
  sortSelect.addEventListener('change', emit);

  container.append(searchInput, rowFilters, countEl);

  return {
    getFilters: () => ({ ...filters }),
    setFilters: (next) => {
      filters = { ...next };
      searchInput.value = filters.search;
      powerSelect.value = filters.powerBand;
      sortSelect.value = filters.sort;
    },
  };
}

export function updateCarListCount(toolbarRoot: HTMLElement, visible: number, total: number): void {
  const countEl = toolbarRoot.querySelector('.garage-toolbar-count');
  if (!countEl) return;
  if (visible === total) {
    countEl.textContent = `${total} voiture${total !== 1 ? 's' : ''}`;
  } else {
    countEl.textContent = `${visible} / ${total} voiture${total !== 1 ? 's' : ''}`;
  }
}

export function renderCarList(
  container: HTMLElement,
  cars: Car[],
  onSelect: (id: number) => void,
  selectedId: number | null = null,
  options?: { emptyFiltered?: boolean }
): void {
  container.innerHTML = '';

  if (cars.length === 0) {
    container.innerHTML = options?.emptyFiltered
      ? `<div class="garage-list-empty"><p>Aucun résultat pour ces filtres.</p></div>`
      : `
      <div class="circuits-empty">
        <span class="circuits-empty-icon" aria-hidden="true">🚗</span>
        <p>Aucune voiture enregistrée.</p>
        <p class="circuits-empty-hint">Cliquez sur « Nouveau » pour en créer une.</p>
      </div>`;
    return;
  }

  const list = document.createElement('div');
  list.className = 'garage-list';
  list.setAttribute('role', 'listbox');
  list.setAttribute('aria-label', 'Liste des voitures');

  for (const c of cars) {
    const power = carPower(c);
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'garage-list-row';
    row.setAttribute('role', 'option');
    row.dataset.id = String(c.id);
    row.title = `${c.name} — Moteur ${c.engine}★, Pneu ${c.tire}★`;

    if (selectedId !== null && c.id === selectedId) {
      row.classList.add('garage-list-row--selected');
      row.setAttribute('aria-selected', 'true');
    } else {
      row.setAttribute('aria-selected', 'false');
    }

    row.innerHTML = `
      <span class="garage-list-row__id">#${c.id}</span>
      <span class="garage-list-row__name">${escapeHtml(c.name)}</span>
      <span class="garage-list-row__power">${power}★</span>`;

    row.addEventListener('click', () => onSelect(c.id!));
    list.appendChild(row);
  }

  container.appendChild(list);
}

export function syncCarListSelection(container: HTMLElement, selectedId: number | null): void {
  container.querySelectorAll('.garage-list-row').forEach((el) => {
    const row = el as HTMLElement;
    const selected = row.dataset.id === String(selectedId);
    row.classList.toggle('garage-list-row--selected', selected);
    row.setAttribute('aria-selected', selected ? 'true' : 'false');
  });
}

export function setCarListLoading(container: HTMLElement, loading: boolean): void {
  if (loading) {
    container.innerHTML =
      '<div class="circuits-loading circuits-loading--compact"><span class="spinner"></span><p>Chargement…</p></div>';
  }
}

export function setCarListError(container: HTMLElement, message: string): void {
  container.innerHTML = `
    <div class="circuits-error">
      <span class="circuits-error-icon" aria-hidden="true">⚠️</span>
      <p>${escapeHtml(message)}</p>
    </div>`;
}

function escapeHtml(s: string): string {
  const div = document.createElement('div');
  div.textContent = s;
  return div.innerHTML;
}
